import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorBoundaryLogHelper } from "../pages/auth/error/useLoggerExc";
import {
  isChunkLoadError,
  reloadForFreshChunks,
} from "../utils/lazy-with-retry";
import ErrorPage from "./ErrorPage";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * @description ErrorBoundary component that catches JavaScript errors in any part of the app
 * and provides a `fallback UI` for `Error`.
 *
 * @author Rajkumar Rathod
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    /** call parent constructor To properly mount the component */
    super(props);
    /** initial state values */
    this.state = { hasError: false };
  }

  /** catch error */
  static getDerivedStateFromError(): ErrorBoundaryState {
    /** update state for catch error */
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    /**
     * A lazy chunk that failed to download is almost always a stale deploy: this
     * tab is still running an old `index.html` whose hashed chunks no longer
     * exist on the CDN. `lazyWithRetry` rescues the imports it wraps, but any
     * chunk error that still reaches this boundary should reload to pick up the
     * fresh build rather than be shown as an error page and logged as an
     * application exception. `reloadForFreshChunks` self-limits (15s cooldown),
     * so a genuinely broken chunk falls through to the ErrorPage instead of
     * looping — it just is not logged as a "FrontEnd Exception".
     */
    if (isChunkLoadError(error)) {
      reloadForFreshChunks();
      return;
    }

    console.error("Error hit ErrorBoundary =>", error, "\nInfo =>", errorInfo);

    /** logger helper using google api */
    if (import.meta.env.VITE_ENABLE_LOGGER === "true") {
      ErrorBoundaryLogHelper(error);
    }
  }

  resetError = () => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorPage resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
